import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

import { DataTable, type Column } from '@/components/DataTable'
import { Card, CardHeader } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { formatMoney, listShifts, listTerminals, type Shift } from '@/features/pos/api'
import { listJobPostingLocations } from '@/features/recruitment/api'

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const STATUS_TONE: Record<Shift['status'], string> = {
  open: 'bg-success/10 text-success',
  closed: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
}

function formatTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

interface Filters {
  location_id?: string
  terminal_id?: string
  status?: string
  page: number
}

/**
 * Who is working which counter, and since when -- the cross-location view of `PosShift`
 * that the cashier's own `/pos/shift` screen does not give an admin. Read-only: opening
 * and closing a shift still happens at the till.
 */
export function PosShifts() {
  const [filters, setFilters] = useState<Filters>({ page: 1 })

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['pos-shifts', filters],
    queryFn: () =>
      listShifts({
        location_id: filters.location_id,
        terminal_id: filters.terminal_id,
        status: filters.status,
        page: filters.page,
      }),
    retry: false,
  })

  const locations = useQuery({
    queryKey: ['job-posting-locations'],
    queryFn: () => listJobPostingLocations(),
    retry: false,
  })

  const terminals = useQuery({
    queryKey: ['pos-terminals', filters.location_id],
    queryFn: () => listTerminals(filters.location_id),
    retry: false,
  })

  const rows = data?.data ?? []
  const meta = data?.meta
  const openCount = rows.filter((row) => row.status === 'open').length

  const set = (patch: Partial<Filters>) =>
    setFilters((current) => ({ ...current, ...patch, page: 1 }))

  const columns: Column<Shift>[] = [
    {
      key: 'terminal_name',
      header: 'Terminal',
      className: 'font-medium',
      cell: (row) => row.terminal_name ?? row.terminal_code ?? row.terminal_id,
    },
    {
      key: 'location_name',
      header: 'Location',
      className: 'text-slate-500 dark:text-slate-400',
      cell: (row) => row.location_name ?? '—',
    },
    {
      key: 'status',
      header: 'Status',
      cell: (row) => (
        <span className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_TONE[row.status]}`}>
          {row.status === 'open' ? 'Open' : 'Closed'}
        </span>
      ),
    },
    {
      key: 'opened_by_name',
      header: 'Opened by',
      cell: (row) => row.opened_by_name ?? '—',
    },
    {
      key: 'opened_at',
      header: 'Opened at',
      className: 'text-slate-500 dark:text-slate-400',
      cell: (row) => formatTime(row.opened_at),
    },
    {
      key: 'closed_by_name',
      header: 'Closed by',
      cell: (row) => row.closed_by_name ?? '—',
    },
    {
      key: 'closed_at',
      header: 'Closed at',
      className: 'text-slate-500 dark:text-slate-400',
      cell: (row) => formatTime(row.closed_at),
    },
    {
      key: 'variance',
      header: 'Variance',
      className: 'text-right tabular-nums',
      cell: (row) => (row.variance ? formatMoney(row.variance) : '—'),
    },
  ]

  const locationOptions = locations.data ?? []
  const terminalOptions = terminals.data ?? []

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-xl font-semibold tracking-tight">POS shifts</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          {meta
            ? `${meta.total} shift${meta.total === 1 ? '' : 's'}, ${openCount} open on this page`
            : 'Loading…'}
        </p>
      </header>

      <Card>
        <CardHeader title="Who is working which counter" />
        <DataTable
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          caption="POS shifts"
          isLoading={isLoading}
          error={error}
          emptyMessage="No shifts match these filters."
          toolbar={
            <div className="flex flex-wrap items-center gap-2">
              <label className="sr-only" htmlFor="shifts-location">
                Location
              </label>
              <select
                id="shifts-location"
                className={SELECT_CLASS}
                value={filters.location_id ?? ''}
                onChange={(event) =>
                  set({ location_id: event.target.value || undefined, terminal_id: undefined })
                }
              >
                <option value="">Any location</option>
                {locationOptions.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </select>

              <label className="sr-only" htmlFor="shifts-terminal">
                Terminal
              </label>
              <select
                id="shifts-terminal"
                className={SELECT_CLASS}
                value={filters.terminal_id ?? ''}
                onChange={(event) => set({ terminal_id: event.target.value || undefined })}
              >
                <option value="">Any terminal</option>
                {terminalOptions.map((terminal) => (
                  <option key={terminal.id} value={terminal.id}>
                    {terminal.name}
                  </option>
                ))}
              </select>

              <label className="sr-only" htmlFor="shifts-status">
                Status
              </label>
              <select
                id="shifts-status"
                className={SELECT_CLASS}
                value={filters.status ?? ''}
                onChange={(event) => set({ status: event.target.value || undefined })}
              >
                <option value="">Any status</option>
                <option value="open">Open now</option>
                <option value="closed">Closed</option>
              </select>

              {(filters.location_id || filters.terminal_id || filters.status) && (
                <Button variant="ghost" size="sm" onClick={() => setFilters({ page: 1 })}>
                  Clear
                </Button>
              )}
            </div>
          }
          pagination={
            meta
              ? {
                  page: meta.page,
                  totalPages: meta.total_pages,
                  onPage: (page) => setFilters((current) => ({ ...current, page })),
                  isFetching,
                }
              : undefined
          }
        />
      </Card>
    </div>
  )
}
